/**
 * Player career projections — computed from matchParticipants / cards (Identity → History → Stats).
 * No stats embedded on players/{playerId}.
 */

import {
  computeCareerWinRate,
  computeSeasonStats,
  computeWinStreak,
} from "../profile/profileDashboardStats";

/**
 * Longest consecutive wins in chronological order (oldest → newest).
 * @param {Array<{ isWin?: boolean }>} cards newest-first
 */
export function computeLongestWinningStreak(cards) {
  const chronological = [...(cards || [])].reverse();
  let best = 0;
  let current = 0;
  chronological.forEach((c) => {
    if (c.isWin) {
      current += 1;
      if (current > best) best = current;
    } else {
      current = 0;
    }
  });
  return best;
}

/**
 * @param {Array<{ durationSec?: number | null }>} cards
 */
export function computeAverageMatchDurationSec(cards) {
  const samples = (cards || [])
    .map((c) => c.durationSec)
    .filter((n) => typeof n === "number" && n > 0);
  if (!samples.length) return null;
  return Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
}

function formatDurationLabel(sec) {
  if (sec == null) return "—";
  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

/**
 * Career + season statistics from match history cards.
 * @param {Array<object>} cards
 */
export function calculateCareerStatistics(cards) {
  const list = cards || [];
  const wins = list.filter((c) => c.isWin).length;
  const losses = list.length - wins;
  const textOf = (c) =>
    `${c.roundName || ""} ${c.resultHeadline || ""} ${c.result || ""} ${c.scoreSummary || ""}`;
  const walkovers = list.filter((c) => /walkover|w\/o/i.test(textOf(c))).length;
  const retirements = list.filter((c) => /retir/i.test(textOf(c))).length;
  let gamesWon = 0;
  let gamesLost = 0;
  list.forEach((c) => {
    if (c.gamesWon != null) gamesWon += Number(c.gamesWon) || 0;
    if (c.gamesLost != null) gamesLost += Number(c.gamesLost) || 0;
    else if (c.scoreSummary) {
      String(c.scoreSummary)
        .split(",")
        .forEach((part) => {
          const m = part.trim().match(/^(\d+)\s*[-–]\s*(\d+)$/);
          if (!m) return;
          const a = Number(m[1]);
          const b = Number(m[2]);
          if (c.isWin) {
            gamesWon += Math.max(a, b);
            gamesLost += Math.min(a, b);
          } else {
            gamesWon += Math.min(a, b);
            gamesLost += Math.max(a, b);
          }
        });
    }
  });
  const avgSec = computeAverageMatchDurationSec(list);
  const season = computeSeasonStats(list);
  const competitions = new Set(list.map((c) => c.competitionId).filter(Boolean));
  const titles = list.filter((c) => c.isWin && /final|championship|title/i.test(textOf(c))).length;
  const runnerUps = list.filter(
    (c) => !c.isWin && /final|championship|title/i.test(textOf(c))
  ).length;

  return {
    matchesPlayed: list.length,
    wins,
    losses,
    walkovers,
    retirements,
    titles,
    runnerUps,
    careerWinPct: computeCareerWinRate(list),
    gamesWon,
    gamesLost,
    currentWinningStreak: computeWinStreak(list),
    longestWinningStreak: computeLongestWinningStreak(list),
    averageMatchDurationSec: avgSec,
    averageMatchDurationLabel: formatDurationLabel(avgSec),
    competitionsPlayed: competitions.size,
    season: {
      year: new Date().getFullYear(),
      ...season,
    },
    lifetime: {
      matchesPlayed: list.length,
      wins,
      losses,
      winPct: computeCareerWinRate(list),
    },
  };
}

/**
 * Tournament history rows grouped by competitionId.
 * @param {Array<object>} cards
 * @param {Record<string, string>} [competitionNameById]
 */
export function projectTournamentHistory(cards, competitionNameById = {}) {
  /** @type {Map<string, { competitionId: string, name: string, matches: number, wins: number, losses: number, lastPlayedAt: number | null }>} */
  const map = new Map();
  (cards || []).forEach((c) => {
    const id = c.competitionId;
    if (!id) return;
    if (!map.has(id)) {
      map.set(id, {
        competitionId: id,
        name: competitionNameById[id] || c.competitionName || "Tournament",
        matches: 0,
        wins: 0,
        losses: 0,
        lastPlayedAt: null,
      });
    }
    const row = map.get(id);
    row.matches += 1;
    if (c.isWin) row.wins += 1;
    else row.losses += 1;
    const at = c.completedAt || 0;
    if (!row.lastPlayedAt || at > row.lastPlayedAt) row.lastPlayedAt = at;
  });
  return [...map.values()].sort((a, b) => (b.lastPlayedAt || 0) - (a.lastPlayedAt || 0));
}

/**
 * Lightweight presentation achievements (not a Firestore collection).
 * @param {ReturnType<typeof calculateCareerStatistics>} stats
 * @param {Array<object>} cards
 */
export function projectAchievements(stats, cards) {
  /** @type {Array<{ id: string, title: string, detail: string }>} */
  const list = [];
  if (stats.matchesPlayed >= 1) {
    list.push({ id: "first_match", title: "First match", detail: "Career match recorded" });
  }
  if (stats.wins >= 1) {
    list.push({ id: "first_win", title: "First win", detail: "Opened the win column" });
  }
  if (stats.wins >= 10) {
    list.push({ id: "wins_10", title: "10 wins", detail: "Double-digit career wins" });
  }
  if (stats.longestWinningStreak >= 3) {
    list.push({
      id: "streak_3",
      title: "Hot streak",
      detail: `${stats.longestWinningStreak}-match winning streak`,
    });
  }
  if (stats.competitionsPlayed >= 1) {
    list.push({
      id: "tournament_debut",
      title: "Tournament debut",
      detail: `${stats.competitionsPlayed} competition${stats.competitionsPlayed === 1 ? "" : "s"}`,
    });
  }
  if (stats.titles >= 1) {
    list.push({
      id: "titles",
      title: "Title holder",
      detail: `${stats.titles} final win${stats.titles === 1 ? "" : "s"}`,
    });
  }
  if (stats.careerWinPct != null && stats.careerWinPct >= 60 && stats.matchesPlayed >= 5) {
    list.push({
      id: "win_rate_60",
      title: "Strong form",
      detail: `${stats.careerWinPct}% career win rate`,
    });
  }
  const favorite = inferFavoriteEvents(cards);
  if (favorite.length) {
    list.push({
      id: "favorite_events",
      title: "Favorite events",
      detail: favorite.join(", "),
    });
  }
  return list.slice(0, 12);
}

/**
 * @param {Array<{ matchType?: string }>} cards
 */
export function inferFavoriteEvents(cards) {
  const counts = {};
  (cards || []).forEach((c) => {
    const t = (c.matchType || "singles").toLowerCase();
    const label =
      t === "doubles" ? "Doubles" : t === "mixed" ? "Mixed" : t === "singles" ? "Singles" : t;
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([k]) => k);
}

/**
 * Client filter for search facets (name query handled by repository).
 * @param {object[]} players
 * @param {{ club?: string, country?: string, state?: string }} facets
 */
export function filterPlayersByFacets(players, facets = {}) {
  const club = (facets.club || "").trim().toLowerCase();
  const country = (facets.country || "").trim().toLowerCase();
  const state = (facets.state || "").trim().toLowerCase();
  return (players || []).filter((p) => {
    if (club && !(p.club || "").toLowerCase().includes(club)) return false;
    if (country) {
      const blob = `${p.country || ""} ${p.countryIso || ""}`.toLowerCase();
      if (!blob.includes(country)) return false;
    }
    if (state && !(p.state || "").toLowerCase().includes(state)) return false;
    return true;
  });
}

/**
 * Milliseconds from player created/updated timestamps (Firestore Timestamp or number).
 * @param {object | null | undefined} player
 */
export function playerRecordTimeMs(player) {
  if (!player) return 0;
  const raw = player.updatedAt ?? player.createdAt ?? player.photoUpdatedAt ?? 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw?.toMillis === "function") return raw.toMillis();
  if (typeof raw?.seconds === "number") return raw.seconds * 1000;
  return 0;
}

/**
 * Discover / directory: only linked RallyAlly accounts (not local/manual roster names).
 * @param {object[]} players
 */
export function filterClaimedRallyAllyPlayers(players) {
  return (players || []).filter((p) => Boolean(p?.userId));
}

/**
 * One row per Auth account — keep the most recently updated player profile.
 * @param {object[]} players
 */
export function dedupePlayersByAccount(players) {
  /** @type {Map<string, object>} */
  const byUser = new Map();
  for (const p of players || []) {
    const uid = p?.userId ? String(p.userId) : "";
    if (!uid) continue;
    const prev = byUser.get(uid);
    if (!prev) {
      byUser.set(uid, p);
      continue;
    }
    const prevT = playerRecordTimeMs(prev);
    const nextT = playerRecordTimeMs(p);
    if (nextT > prevT) {
      byUser.set(uid, p);
    } else if (nextT === prevT) {
      // Stable tie-break: prefer lexicographically greater playerId (newer-looking ids often later).
      if (String(p.playerId || "") > String(prev.playerId || "")) {
        byUser.set(uid, p);
      }
    }
  }
  return Array.from(byUser.values());
}

/**
 * Paginate an in-memory list (cursor = offset index).
 * @param {unknown[]} items
 * @param {{ limit?: number, offset?: number }} options
 */
export function paginateItems(items, options = {}) {
  const limit = Math.min(Math.max(options.limit || 20, 1), 50);
  const offset = Math.max(options.offset || 0, 0);
  const slice = items.slice(offset, offset + limit);
  return {
    items: slice,
    offset,
    nextOffset: offset + slice.length < items.length ? offset + slice.length : null,
    hasMore: offset + slice.length < items.length,
    total: items.length,
  };
}

function memberLabel(member) {
  return String(
    member?.displayName ||
      member?.name ||
      [member?.firstName, member?.lastName].filter(Boolean).join(" ") ||
      ""
  ).trim();
}

function uniqueSideMembers(members, category) {
  const seen = new Set();
  const unique = [];
  for (const member of members || []) {
    const key = String(member?.playerId || memberLabel(member) || "")
      .trim()
      .toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(member);
  }
  if (category === "singles") return unique.slice(0, 1);
  return unique;
}

/**
 * Build teams / snapshots for a completed draw fixture.
 * @param {object} competition
 * @param {object} fixture
 */
export function buildFixtureSideSnapshots(competition, fixture) {
  const entries = Array.isArray(competition?.managedEntries) ? competition.managedEntries : [];
  const byId = new Map(entries.map((e) => [e.entryId, e]));

  const side = (entryId, label, team) => {
    const entry = entryId ? byId.get(entryId) : null;
    const rawMembers = entry?.members?.length
      ? entry.members
      : (entry?.memberPlayerIds || []).map((playerId, i) => ({
          playerId,
          displayName: label || `Player ${i + 1}`,
        }));
    const members = uniqueSideMembers(rawMembers, entry?.category);
    if (!members.length) {
      return {
        name: label || "TBD",
        type: "singles",
        slots: [
          {
            registryEntryId: entryId || `anon_${team}`,
            playerId: null,
            displayName: label || "TBD",
            avatar: null,
            team,
            position: 0,
          },
        ],
      };
    }
    const type = members.length > 1 ? "doubles" : "singles";
    const memberNames = members.map(
      (m) => m.displayName || m.name || [m.firstName, m.lastName].filter(Boolean).join(" ")
    );
    return {
      name:
        (type === "singles"
          ? memberNames.find(Boolean) || label || entry?.label
          : label || entry?.label || memberNames.filter(Boolean).join(" / ")) || "TBD",
      type,
      slots: members.map((m, i) => ({
        registryEntryId: entry?.entryId || `entry_${team}_${i}`,
        playerId: m.playerId || null,
        displayName: memberLabel(m) || label || "Player",
        avatar: m.avatar || null,
        team,
        position: i,
      })),
    };
  };

  const teamA = side(fixture.entryAId, fixture.labelA, "A");
  const teamB = side(fixture.entryBId, fixture.labelB, "B");
  const snapshots = [...teamA.slots, ...teamB.slots];
  return { teamA, teamB, snapshots };
}

/**
 * Idempotent key for tournament fixture → match history.
 */
export function fixtureHistoryIdempotencyKey(competitionId, drawId, fixtureId) {
  return `fixture:${competitionId}:${drawId}:${fixtureId}`;
}
